"use client";

import { useElementSize } from "../../hooks/use-element-size";

/**
 * Minimum height one category row needs before its axis label collides with the
 * label above it. A tick is 12px type, and a row also carries the bar and the
 * gap around it.
 */
const MIN_ROW_HEIGHT = 24;

/**
 * How many category rows fit in a measured region.
 *
 * A `rows` bar chart draws every category it is handed, so a tile shorter than
 * its data is tall paints the axis labels on top of each other — eight names in
 * a hundred pixels is unreadable, and no amount of truncation fixes it because
 * the collision is vertical. The answer is fewer bars, never smaller type, and
 * only the rendered region knows how many that is.
 *
 * Attach `ref` to the element the chart fills, then slice the data to `budget`
 * and name the remainder. `budget` is `null` until the region reports a size:
 * draw everything in that frame rather than dropping rows against a height of
 * zero. Put the ref on the CHART's own box, not on a wrapper that also holds
 * the caption — a caption that appears takes its height out of the chart, which
 * is exactly the space the next budget has to be measured against.
 *
 * The floor is one row. A region can always be dragged shorter than any
 * minimum, and one bar is still a chart where zero is a blank tile.
 *
 * `minRowHeight` is the pitch of the rows being budgeted. The default is a
 * bar chart's category row; a list form with taller rows passes its own
 * (`FUNNEL_BAR_ROW_HEIGHT`), or the budget overstates what fits.
 */
export function useCategoryRowBudget(minRowHeight: number = MIN_ROW_HEIGHT): {
  ref: ReturnType<typeof useElementSize<HTMLDivElement>>["ref"];
  budget: number | null;
} {
  const { ref, size } = useElementSize<HTMLDivElement>();
  // Zero reads as "not measured yet" as well as "genuinely empty", so both
  // answer null and the caller draws its full set for that frame.
  const budget =
    size.height === 0 ? null : Math.max(1, Math.floor(size.height / minRowHeight));
  return { ref, budget };
}
